import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import type { Dispatch, SetStateAction } from 'react';

import { menuFullWidthByCss, menuWidthByCss } from '@common/DesignLayout/DesignMenu/constantts';
import { prefix, RoutePath } from '@components/AppRouter/constants';
import { designMenuStore } from '@stores/designMenuStore';
import { userStore } from '@stores/userStore';
import { getPathsFromRoutes } from '@utils';

import { filterRoutes } from '../../../AppRouter/routeConfig/utils';
import { DesignSubMenu } from './DesignSubMenu';
import { IconName } from './interfaces';
import type { DesignMenuRouteInterface, DownMenuInterface } from './interfaces';

import styles from './DesignMenu.module.scss';

const idx = prefix ? 2 : 1;

function DesignMenuComponent({
  menuWidth,
  setMenuWidth,
}: {
  setMenuWidth: Dispatch<SetStateAction<number>>;
  menuWidth: number;
}): JSX.Element {
  const { menuRefData } = designMenuStore;
  const { user } = userStore;

  const menu: (DesignMenuRouteInterface | undefined)[] = menuRefData.map((el) => {
    switch (el?.key) {
      case 'dictionary': {
        return {
          ...el,
          iconName: IconName.DICTIONARY,
          includedRoutes: getPathsFromRoutes([el]),
          key: 'dictionary',
        };
      }
      case 'publication': {
        return {
          ...el,
          iconName: IconName.PUBLICATION,
          path: el.path,
          includedRoutes: [RoutePath.PREPARATION_OF_REPORTS],
          key: 'publication',
        };
      }
      case 'tree': {
        return {
          ...el,
          iconName: IconName.REPORTING_TREE,
          includedRoutes: [RoutePath.REPORTING_TREE],
          key: 'tree',
        };
      }
      case 'dataControl': {
        return {
          ...el,
          iconName: IconName.DATA_CONTROL,
          includedRoutes: [RoutePath.DATA_CONTROL],
          key: 'dataControl',
        };
      }
      case 'etlProcess': {
        return {
          ...el,
          iconName: IconName.LOGS,
          includedRoutes: [RoutePath.LOGS],
          key: 'etlProcess',
        };
      }
      case 'security': {
        return {
          ...el,
          iconName: IconName.SECURITY,
          includedRoutes: [RoutePath.AUDIT, RoutePath.USER_ROLE_REPORT],
          key: 'security',
        };
      }
    }
  }, []);

  const {
    addMenuData,
    menuData,
    rmMenuData,
    menuData: { length: menuDataLength },
  } = designMenuStore;

  const {
    push,
    location: { pathname },
  } = useHistory();

  const downSubMenu = useMemo(
    () =>
      filterRoutes<DownMenuInterface>({
        routes: [
          {
            iconName: 'settings',
            key: 'settings',
            title: 'Настройки',
            resourceName: ['CmUserDataFilter', 'CmSettings'],
            includedRoutes: [RoutePath.SETTING_USER_ROLES, RoutePath.SETTINGS],
            routes: [
              {
                title: 'Настройка ролей пользователей',
                path: RoutePath.SETTING_USER_ROLES,
                resourceName: 'CmUserDataFilter',
              },
              {
                title: 'Системные настройки',
                resourceName: 'CmSettings',
                path: RoutePath.SETTINGS,
              },
            ],
          },
          {
            iconName: 'profile',
            key: 'profile',
            title: 'Личный кабинет',
            includedRoutes: [RoutePath.ACCOUNT],
            routes: [
              {
                title: 'Данные пользователя',
                path: RoutePath.ACCOUNT,
              },
              {
                withTopDivider: true,
                title: 'Выйти из профиля',
                path: 'logout',
              },
            ],
          },

          { iconName: 'arrowToRight', key: 'openArrow' },
        ],
        user,
      }),
    [user],
  );

  const onClickMenuItem = useCallback(
    (item: DesignMenuRouteInterface) => {
      if (item.routes && item.routes.length > 0) {
        addMenuData(item);
      } else if (item.path) {
        rmMenuData(true);
        if (pathname !== item.path) {
          push(item.path);
        }
      }
    },
    [addMenuData, rmMenuData, pathname, push],
  );

  const createMenuItem = useCallback(
    (item) => {
      const { iconName, key, title, hidden, includedRoutes, onClick } = item;

      return (
        <div className={cm(styles.icon, { [styles.hidden]: hidden })} key={key}>
          <div
            tabIndex={-1}
            role="button"
            className={cm(styles.linkWrapper, {
              [styles.selected]:
                pathname.split('/')[idx] &&
                includedRoutes?.some((routePath: string) => pathname.split('/')[idx] === routePath.split('/')[idx]) &&
                menuDataLength === 0,
              [styles.viewSubMenu]: menuDataLength > 0 && menuData[0].key === key,
            })}
            onClick={() => (onClick ? onClick() : onClickMenuItem(item))}
            onKeyDown={() => onClickMenuItem(item)}
          >
            <div className={styles.leftYellowLine} />
            <div className={styles.content}>
              <Icon name={iconName} noFill />
              {menuWidth === menuFullWidthByCss && <p>{title}</p>}
            </div>
            <div className={styles.upperRightAnchor} />
            <div className={styles.downRightAnchor} />
          </div>
          {(menuDataLength > 0 ? menuData[0].key !== key : title) && menuWidth !== menuFullWidthByCss && (
            <div className={styles.tooltip}>
              <span>{title}</span>
            </div>
          )}
        </div>
      );
    },
    [pathname, menuDataLength, menuData, menuWidth, onClickMenuItem],
  );

  const delimiter = useCallback(
    (key: string) => (
      <div className={cm(styles.delimiter, styles.icon)} key={key}>
        <div className={styles.linkWrapper} />
      </div>
    ),
    [],
  );

  const toggleMenuWidth = useCallback(() => {
    setMenuWidth(menuWidth === menuWidthByCss ? menuFullWidthByCss : menuWidthByCss);
  }, [menuWidth, setMenuWidth]);

  const upMenu = useMemo(
    () => (
      <>
        <div
          className={cm(styles.icon, styles.logotype)}
          onClick={() => onClickMenuItem({ path: RoutePath.HOME, key: 'home' })}
        >
          <Icon name="gnivcLogo" />
        </div>
        <div onClick={toggleMenuWidth} className={styles.icon}>
          <Icon name={menuWidth === menuWidthByCss ? 'arrowToRight' : 'arrowToLeft'} />
        </div>
      </>
    ),
    [menuWidth, onClickMenuItem, toggleMenuWidth],
  );

  const middleMenu = useMemo(() => menu.filter((el) => el).map((item) => createMenuItem(item)), [createMenuItem, menu]);

  const downMenu = useMemo(() => {
    const data = [...downSubMenu];
    // скрывает стрелочку вправо, если меню открыто
    data[data.length - 1].hidden = true;

    const feedback = data.shift();

    if (data && data.length === 1) {
      return [createMenuItem(feedback), ...data.map((item) => createMenuItem(item))];
    }
    return [delimiter('delimiter'), createMenuItem(feedback), ...data.map((item) => createMenuItem(item))];
  }, [createMenuItem, delimiter, downSubMenu]);

  return (
    <>
      <div
        className={cm(styles.designMenu, 'design-menu-level-1', {
          [styles.full]: menuWidth === menuFullWidthByCss,
        })}
      >
        <div className={styles.upMenu}>{upMenu}</div>
        <div className={styles.middleMenu}>{middleMenu}</div>
        <div className={styles.bottomAction}>{downMenu}</div>
      </div>
      <DesignSubMenu menuWidth={menuWidth} />
    </>
  );
}

export const DesignMenu = observer(DesignMenuComponent);
